'use client'

import React, { useMemo } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import {
	floorTint,
	percentOfFloor,
	squarifyWithMinimum,
	tileWeight,
	treemapHeight,
	type CollectionBid,
	type InscriptionBidGroup
} from '@/utils/bids'
import BidAmount from './bid-amount'
import CollectionArt from './collection-art'
import InscriptionThumb from './inscription-thumb'
import { quickViewPayload } from './quick-view'
import styles from './bids.module.css'

// Nothing on the board is smaller than a comfortable pointer/tap target.
const MIN_TILE_W = 59
const MIN_TILE_H = 51
const MIN_ITEM_W = 46
const MIN_ITEM_H = 46

// 11px is the floor for functional text.
const labelSize = (w: number, h: number, max = 13) =>
	Math.round(Math.max(11, Math.min(max, Math.min(w, h) / 8)))

const spoken = (sats: number) => {
	return `${(sats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })} BTC`
}

export function CollectionBidsTreemap({
	bids,
	width,
	pulses,
	selectedSlug,
	onSelect,
	onShowAll,
	entering
}: {
	bids: CollectionBid[]
	width: number
	pulses: Record<string, number>
	selectedSlug?: string | null
	onSelect?: (slug: string) => void
	onShowAll?: () => void
	/** True while the page is entering: tiles settle in a cascade. */
	entering?: boolean
}) {
	// Deepest books first: area is quantity × price, not the headline bid.
	const ordered = useMemo(
		() =>
			[...bids].sort(
				(a, b) => tileWeight(b.priceSats, b.quantity) - tileWeight(a.priceSats, a.quantity)
			),
		[bids]
	)
	const height = treemapHeight(width, ordered.length)
	const layout = useMemo(
		() =>
			squarifyWithMinimum(
				ordered.map((b) => tileWeight(b.priceSats, b.quantity)),
				width,
				height,
				MIN_TILE_W,
				MIN_TILE_H
			),
		[ordered, width, height]
	)
	if (!width) return <div style={{ height }} />
	if (!ordered.length) return null
	return (
		<div className="relative w-full" style={{ height }} data-testid="bids-collection-treemap">
			{layout.rects.map((r, i) => {
				const bid = ordered[r.index]
				const pct = percentOfFloor(bid.priceSats, bid.floorSats)
				const roomy = r.w >= 96 && r.h >= 72
				return (
					<div
						key={bid.id}
						className={classNames('absolute', entering && styles.tileEnter)}
						style={
							{
								left: r.x,
								top: r.y,
								width: r.w,
								height: r.h,
								padding: '0 3px 3px 0',
								'--i': i
							} as React.CSSProperties
						}
					>
						<Link
							href={`/collection/${bid.slug}`}
							onMouseEnter={() => onSelect?.(bid.slug)}
							onFocus={() => onSelect?.(bid.slug)}
							aria-label={`${bid.collection.name}, offer ${spoken(bid.priceSats)}${
								bid.quantity > 1 ? `, ${bid.quantity} offers` : ''
							}${pct !== null ? `, ${Math.abs(100 - pct)}% ${pct > 100 ? 'Above' : 'Below'} Floor` : ''}`}
							data-pulse={pulses[bid.id] ? 'true' : undefined}
							key={pulses[bid.id] || 0}
							className={classNames(
								styles.tile,
								'group relative block h-full w-full overflow-hidden rounded-lg border bg-muted',
								selectedSlug === bid.slug ? 'border-[#9E77ED] ring-1 ring-[#9E77ED]' : 'border-border'
							)}
						>
							<CollectionArt
								collection={bid.collection}
								imageClassName="transition-transform duration-500 group-hover:scale-105"
							/>
							{roomy && (
								<div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-2 bg-gradient-to-b from-black/75 to-transparent px-2 pb-5 pt-1.5 text-[11px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
									<span className="truncate">{bid.collection.name}</span>
									{pct !== null ? <span className="shrink-0 text-white/85">{Math.abs(100 - pct)}% {pct > 100 ? 'Above' : 'Below'} Floor</span> : null}
								</div>
							)}
							<div
								className="absolute inset-x-0 bottom-0 flex items-center gap-1 px-2 py-1.5 font-semibold leading-none text-white"
								style={{ fontSize: labelSize(r.w, r.h), backgroundColor: floorTint(pct) }}
							>
								<BidAmount sats={bid.priceSats} iconClassName="size-[1em]" />
								{bid.quantity > 1 && r.w > 110 ? (
									<span className="ml-auto shrink-0 text-white/85">×{bid.quantity}</span>
								) : null}
							</div>
						</Link>
					</div>
				)
			})}
			{layout.overflowRect && layout.overflow > 0 && (
				<div
					className="absolute"
					style={{
						left: layout.overflowRect.x,
						top: layout.overflowRect.y,
						width: layout.overflowRect.w,
						height: layout.overflowRect.h,
						padding: '0 3px 3px 0'
					}}
				>
					<button
						type="button"
						onClick={onShowAll}
						data-testid="bids-more-tile"
						className="flex h-full w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card text-center text-foreground transition-colors hover:bg-accent"
					>
						<span className="text-sm font-semibold tabular-nums">+{layout.overflow}</span>
						<span className="text-[11px] text-muted-foreground">more</span>
					</button>
				</div>
			)}
		</div>
	)
}

export function InscriptionBidsTreemap({
	groups,
	width,
	pulses,
	entering
}: {
	groups: InscriptionBidGroup[]
	width: number
	pulses: Record<string, number>
	/** True while the page is entering: groups settle in a cascade. */
	entering?: boolean
}) {
	const openInscription = useInscriptionModal()
	const count = groups.reduce((n, g) => n + g.bids.length, 0)
	const height = treemapHeight(width, Math.max(count / 3, groups.length * 2), {
		min: 420,
		max: 1400
	})
	const headerH = 28
	// A group needs room for its header plus one minimum item.
	const outer = useMemo(
		() =>
			squarifyWithMinimum(
				groups.map((g) => tileWeight(g.totalSats)),
				width,
				height,
				MIN_ITEM_W * 2 + 8,
				MIN_ITEM_H + headerH + 8
			),
		[groups, width, height]
	)
	if (!width) return <div style={{ height }} />
	return (
		<div className="relative w-full" style={{ height }} data-testid="bids-inscription-treemap">
			{outer.rects.map((r, i) => {
				const group = groups[r.index]
				const innerW = Math.max(0, r.w - 3 - 2)
				const innerH = Math.max(0, r.h - 3 - headerH - 2)
				const inner = squarifyWithMinimum(
					group.bids.map((b) => tileWeight(b.priceSats)),
					innerW,
					innerH,
					MIN_ITEM_W,
					MIN_ITEM_H
				)
				return (
					<div
						key={group.slug}
						className={classNames('absolute', entering && styles.tileEnter)}
						style={
							{
								left: r.x,
								top: r.y,
								width: r.w,
								height: r.h,
								padding: '0 3px 3px 0',
								'--i': i
							} as React.CSSProperties
						}
					>
						<div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-card">
							<div className="flex h-7 shrink-0 items-center gap-1.5 px-2">
								<Link
									href={`/collection/${group.slug}`}
									className="flex min-w-0 items-center gap-1.5 hover:underline"
								>
									<span className="relative size-4 shrink-0 overflow-hidden rounded border border-border/60 bg-muted">
										<CollectionArt collection={group.collection} />
									</span>
									<span className="truncate text-[11px] font-semibold leading-none text-foreground">
										{group.collection.name}
									</span>
								</Link>
								<span className="ml-auto flex shrink-0 items-center text-[11px] font-semibold leading-none text-foreground">
									<BidAmount sats={group.totalSats} iconClassName="size-2.5" />
								</span>
							</div>
							<div className="relative min-h-0 flex-1 overflow-hidden bg-muted/40">
								{inner.rects.map((ir) => {
									const bid = group.bids[ir.index]
									const pct = percentOfFloor(bid.priceSats, bid.listedSats)
									return (
										<div
											key={bid.id}
											className="absolute"
											style={{
												left: ir.x,
												top: ir.y,
												width: ir.w,
												height: ir.h,
												padding: '0 2px 2px 0'
											}}
										>
											<button
												type="button"
												onClick={(e) =>
													openInscription(quickViewPayload(bid.inscriptionId, bid.collection), e)
												}
												aria-label={`${bid.name}, offer ${spoken(bid.priceSats)}${
													bid.listedSats ? `, listed at ${spoken(bid.listedSats)}` : ''
												}`}
												data-pulse={pulses[bid.id] ? 'true' : undefined}
												key={pulses[bid.id] || 0}
												className={classNames(
													styles.tile,
													'group relative block h-full w-full overflow-hidden rounded-md border border-border/70 bg-muted text-left'
												)}
											>
												<InscriptionThumb
													id={bid.inscriptionId}
													collection={bid.collection}
													hoverScale
												/>
												<span
													className="absolute inset-x-0 bottom-0 flex items-center gap-1 px-1.5 py-1 font-semibold leading-none text-white"
													style={{
														fontSize: labelSize(ir.w, ir.h, 12),
														backgroundColor: floorTint(pct)
													}}
												>
													<BidAmount sats={bid.priceSats} iconClassName="size-[1em]" />
												</span>
											</button>
										</div>
									)
								})}
								{inner.overflowRect && inner.overflow > 0 && (
									<div
										className="absolute"
										style={{
											left: inner.overflowRect.x,
											top: inner.overflowRect.y,
											width: inner.overflowRect.w,
											height: inner.overflowRect.h,
											padding: '0 2px 2px 0'
										}}
									>
										<Link
											href={`/collection/${group.slug}`}
											aria-label={`${inner.overflow} more offers in ${group.collection.name}`}
											className="flex h-full w-full items-center justify-center rounded-md border border-dashed border-border bg-card text-xs font-semibold tabular-nums text-foreground transition-colors hover:bg-accent"
										>
											+{inner.overflow}
										</Link>
									</div>
								)}
							</div>
						</div>
					</div>
				)
			})}
			{outer.overflowRect && outer.overflow > 0 && (
				<div
					className="absolute"
					style={{
						left: outer.overflowRect.x,
						top: outer.overflowRect.y,
						width: outer.overflowRect.w,
						height: outer.overflowRect.h,
						padding: '0 3px 3px 0'
					}}
				>
					<div className="flex h-full w-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card text-center">
						<span className="text-sm font-semibold tabular-nums text-foreground">
							+{outer.overflow}
						</span>
						<span className="text-[11px] text-muted-foreground">collections</span>
					</div>
				</div>
			)}
		</div>
	)
}
